import { Controller } from "@hotwired/stimulus"
import { startsInSwipeBackEdge } from "./swipe_back_controller"

/**
 * Swipe Reply Controller
 *
 * A reusable controller for swipe-to-trigger actions, specifically built for
 * WhatsApp/Signal style swipe-to-reply on chat messages.
 * Swiping right pulls the element, and releasing past a threshold triggers an event.
 *
 * Usage:
 *   <div data-controller="swipe-reply"
 *        data-swipe-reply-threshold-value="60">
 *     <div data-swipe-reply-target="content">
 *       <!-- Message content -->
 *     </div>
 *   </div>
 *
 * Events dispatched:
 *   - "swipe-reply:trigger" (bubbles: true) - fired when the threshold is reached on release.
 */
export default class extends Controller {
  static targets = ["content"]
  static values = {
    threshold: { type: Number, default: 50 },  // px to trigger reply
    max:       { type: Number, default: 80 },  // max visual drag distance
  }

  connect() {
    this.startX = 0
    this.startY = 0
    this.currentX = 0
    this.isSwiping = false
    this.isScrolling = false
    this.pointerDown = false

    this._onPointerDown = this.onPointerDown.bind(this)
    this._onPointerMove = this.onPointerMove.bind(this)
    this._onPointerUp = this.onPointerUp.bind(this)
    this._onDragStart = this.onDragStart.bind(this)
    this._onClick = this.onClick.bind(this)

    window.addEventListener("pointerup", this._onPointerUp, { passive: true })
    window.addEventListener("pointercancel", this._onPointerUp, { passive: true })

    if (this.hasContentTarget) {
      this.contentTarget.addEventListener("pointerdown", this._onPointerDown, { passive: true })
      this.contentTarget.addEventListener("pointermove", this._onPointerMove, { passive: true })
      this.contentTarget.addEventListener("dragstart", this._onDragStart)
      this.contentTarget.addEventListener("click", this._onClick, { capture: true })
    }
  }

  disconnect() {
    window.removeEventListener("pointerup", this._onPointerUp)
    window.removeEventListener("pointercancel", this._onPointerUp)

    if (this.hasContentTarget) {
      this.contentTarget.removeEventListener("pointerdown", this._onPointerDown)
      this.contentTarget.removeEventListener("pointermove", this._onPointerMove)
      this.contentTarget.removeEventListener("dragstart", this._onDragStart)
      this.contentTarget.removeEventListener("click", this._onClick, { capture: true })
    }
  }

  onClick(e) {
    if (this.wasSwiping) {
      e.preventDefault()
      e.stopPropagation()
      this.wasSwiping = false
    }
  }

  onDragStart(e) {
    e.preventDefault()
  }

  onPointerDown(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return // only left click
    // A touch at the panel's left edge is the swipe back, not a reply.
    if (startsInSwipeBackEdge(e)) return

    this.startX = e.clientX
    this.startY = e.clientY
    this.currentX = 0
    this.isSwiping = false
    this.isScrolling = false
    this.pointerDown = true

    if (this.hasContentTarget) {
      this.contentTarget.style.transition = ""
    }
  }

  onPointerMove(e) {
    if (!this.pointerDown || this.isScrolling || !this.hasContentTarget) return

    const dx = e.clientX - this.startX
    const dy = e.clientY - this.startY

    // Determine direction on first significant move
    if (!this.isSwiping && !this.isScrolling) {
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 5) {
        this.isScrolling = true
        return
      }
      if (Math.abs(dx) > 5) {
        this.isSwiping = true
        if (this.hasContentTarget) {
          try {
            this.contentTarget.setPointerCapture(e.pointerId)
          } catch (err) {}
        }
      }
    }

    if (!this.isSwiping) return

    let offset = dx

    // Only allow right swipe (positive dx)
    if (offset < 0) {
      offset = offset * 0.1 // Heavy resistance if swiping left
    } else {
      // Rubber-band effect as we approach max
      if (offset > this.maxValue) {
        offset = this.maxValue + (offset - this.maxValue) * 0.2
      }
    }

    this.currentX = offset
    this.contentTarget.style.transform = `translateX(${offset}px)`
  }

  onPointerUp(e) {
    if (!this.pointerDown) return
    this.pointerDown = false
    
    if (this.hasContentTarget) {
      try {
        this.contentTarget.releasePointerCapture(e.pointerId)
      } catch (err) {}
    }
    
    if (!this.isSwiping || !this.hasContentTarget) return

    // Mark that we just swiped so the click handler can intercept the subsequent click event
    this.wasSwiping = true
    setTimeout(() => this.wasSwiping = false, 0)

    const easing = "transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
    this.contentTarget.style.transition = easing
    this.contentTarget.style.transform = "" // Always snap back

    if (this.currentX > this.thresholdValue) {
      // Trigger the reply event
      this.dispatch("trigger", { bubbles: true })
    }

    this.contentTarget.addEventListener("transitionend", () => {
      this.contentTarget.style.transition = ""
    }, { once: true })

    this.isSwiping = false
  }
}
